import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { API_URL } from '../services/api';
import toast from '../utils/toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const CustomPageSubmissionDetail = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { checkPermission, user } = useAuth();

  // Helper function to get full file URL
  const getFullFileUrl = (relativePath) => {
    if (!relativePath) return '';
    // If it's already a full URL, return as is
    if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
      return relativePath;
    }
    // Remove leading slash if present
    const cleanPath = relativePath.startsWith('/') ? relativePath.slice(1) : relativePath;
    // Get base URL without /api
    const baseUrl = API_URL.replace('/api', '');
    return `${baseUrl}/${cleanPath}`;
  };
  
  const [submission, setSubmission] = useState(null);
  const [customPage, setCustomPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submissionComments, setSubmissionComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  // Document Inventory State
  const [documentInventory, setDocumentInventory] = useState([]);
  const [documentDate, setDocumentDate] = useState('');
  const [documentFile, setDocumentFile] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);

  const canUpdate = checkPermission('Update Custom Page Submission');
  const canDelete = checkPermission('Delete Custom Page Submission');

  useEffect(() => {
    fetchSubmissionDetails();
  }, [submissionId]);

  useEffect(() => {
    // Fetch document inventory if enabled
    if (customPage?.allowDocumentInventory) {
      fetchDocumentInventory();
    }
  }, [customPage, submissionId]);

  useEffect(() => {
    // Initialize submission comments (only additional comments, not the initial comment)
    if (submission) {
      // Only add submissionComments array, not the initial comment
      // The initial comment is displayed separately
      if (submission.submissionComments && Array.isArray(submission.submissionComments)) {
        setSubmissionComments(submission.submissionComments);
      } else {
        setSubmissionComments([]);
      }
    } else {
      setSubmissionComments([]);
    }
  }, [submission]);

  const fetchSubmissionDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/custom-pages/submissions/${submissionId}`);
      
      if (response.data.success) {
        setSubmission(response.data.data);
        // Use the populated custom page data directly
        if (response.data.data.customPage) {
          setCustomPage(response.data.data.customPage);
          // Debug log for Document Inventory
          console.log('=== DOCUMENT INVENTORY DEBUG (FRONTEND) ===');
          console.log('Full Response:', response.data.data);
          console.log('Custom Page Object:', response.data.data.customPage);
          console.log('Custom Page Type:', typeof response.data.data.customPage);
          console.log('Allow Document Inventory:', response.data.data.customPage.allowDocumentInventory);
          console.log('Allow Document Inventory Type:', typeof response.data.data.customPage.allowDocumentInventory);
          console.log('Document Inventory Settings:', response.data.data.customPage.documentInventorySettings);
          console.log('Will Show Upload Tab?', !!response.data.data.customPage.allowDocumentInventory);
        } else {
          console.error('=== ERROR: Custom Page not found in response ===');
          console.log('Response data:', response.data.data);
        }
        
        // Debug log for Checklist
        console.log('Checklist in Detail View:', response.data.data.documentVerifications);
      }
    } catch (error) {
      console.error('Error fetching submission details:', error);
      toast.error('Failed to fetch submission details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await api.put(`/custom-pages/submissions/${submissionId}`, {
        status: newStatus
      });
      
      if (response.data.success) {
        toast.success('Status updated successfully');
        setSubmission(response.data.data);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this submission?')) {
      return;
    }

    try {
      const response = await api.delete(`/custom-pages/submissions/${submissionId}`);
      
      if (response.data.success) {
        toast.success('Submission deleted successfully');
        navigate('/forms-and-tasks');
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast.error('Failed to delete submission');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const response = await api.post(`/custom-pages/submissions/${submissionId}/comments`, {
        text: newComment.trim()
      });
      
      if (response.data.success) {
        setNewComment('');
        // Refresh submission data
        await fetchSubmissionDetails();
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const fetchDocumentInventory = async () => {
    try {
      const response = await api.get(`/custom-pages/submissions/${submissionId}/document-inventory`);
      if (response.data.success) {
        setDocumentInventory(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching document inventory:', error);
    }
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    if (!documentFile || !documentDate) {
      toast.error('Please select a file and document date');
      return;
    }

    setUploadingDocument(true);
    try {
      const formData = new FormData();
      formData.append('file', documentFile);
      formData.append('documentDate', documentDate);

      const response = await api.post(
        `/custom-pages/submissions/${submissionId}/document-inventory`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (response.data.success) {
        toast.success('Document uploaded successfully');
        setDocumentDate('');
        setDocumentFile(null);
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
        await fetchDocumentInventory();
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.response?.data?.message || 'Failed to upload document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await api.delete(
        `/custom-pages/submissions/${submissionId}/document-inventory/${documentId}`
      );

      if (response.data.success) {
        toast.success('Document deleted successfully');
        await fetchDocumentInventory();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderFieldValue = (field, value) => {
    if (!value) return '-';

    switch (field.type) {
      case 'upload':
        if (Array.isArray(value) && value.length > 0) {
          return (
            <div className="space-y-1">
              {value.map((file, index) => {
                // Handle different file object structures
                const fileUrl = file.fileUrl || file.url || '';
                const fileName = file.fileName || file.name || file.originalName || 'File';
                
                if (!fileUrl) {
                  return (
                    <div key={index} className="text-sm text-gray-500">
                      {fileName} (No URL available)
                    </div>
                  );
                }
                
                return (
                  <div key={index} className="text-sm text-blue-600 hover:text-blue-800">
                    <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                      {fileName}
                    </a>
                  </div>
                );
              })}
            </div>
          );
        }
        return 'No files uploaded';

      case 'masterdata':
        if (value && typeof value === 'object') {
          return value.clientName || 'N/A';
        }
        return value || '-';

      case 'user':
        if (value && typeof value === 'object') {
          return `${value.firstName} ${value.lastName}`;
        }
        return value || '-';

      case 'date':
        return (value);

      case 'multitext':
        return (
          <div className="whitespace-pre-wrap text-sm">
            {value}
          </div>
        );

      default:
        return value;
    }
  };

  // Helper function to get field value from submission data
  const getFieldValue = (fieldName) => {
    if (!submission || !submission.data) return null;
    
    // Handle both Map and object data structures
    if (submission.data instanceof Map) {
      return submission.data.get(fieldName);
    } else {
      return submission.data[fieldName];
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Submission not found</h2>
          <p className="text-gray-600 mt-2">The submission you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/forms-and-tasks')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to Forms & Tasks
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/forms-and-tasks')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Forms & Tasks
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Submission Details</h1>
            <p className="text-gray-600 mt-2">
              {customPage?.name}
            </p>
          </div>
          <div className="flex space-x-4">
            {canUpdate && (
              <>
                <button
                  onClick={() => navigate(`/admin/settings/custom-page-submissions/${customPage?._id}/edit/${submissionId}`)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Edit
                </button>
                <select
                  value={submission.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="draft">Draft</option>
                  <option value="submitted">Submitted</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="completed">Completed</option>
                </select>
              </>
            )}
            {canDelete && (
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="space-y-8">
          {/* Submission Info */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Submission Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                  submission.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                  submission.status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                  submission.status === 'approved' ? 'bg-green-100 text-green-800' :
                  submission.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-purple-100 text-purple-800'
                }`}>
                  {submission.status}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Submitted By</label>
                <p className="text-sm text-gray-900">
                  {submission.submittedBy?.firstName} {submission.submittedBy?.lastName}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Submitted Date</label>
                <p className="text-sm text-gray-900">{formatDate(submission.createdAt)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Deadline</label>
                <p className="text-sm text-gray-900">
                  {submission.deadline ? formatDate(submission.deadline) : '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Form Data - 3 columns for normal fields, 2 columns for multitext */}
          {customPage && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Form Data</h2>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
                {customPage.fields.map((field, index) => {
                  // Check if field should be visible based on deadline type and isDeadlineField
                  let shouldShowField = true;
                  
                  if (field.isDeadlineField && submission.deadline?.type) {
                    if (submission.deadline.type === 'personal') {
                      // Personal deadline: Only show to creator
                      const submitterId = submission.submittedBy?._id || submission.submittedBy;
                      shouldShowField = user && (user.id === submitterId || user._id === submitterId);
                    } else if (submission.deadline.type === 'group') {
                      // Group deadline: Only show to users in the same group
                      const submitterGroupId = submission.submittedBy?.groupId || submission.submittedBy?.group?._id;
                      const userGroupId = user?.groupId || user?.group?._id;
                      shouldShowField = submitterGroupId && userGroupId && submitterGroupId.toString() === userGroupId.toString();
                    }
                  }
                  
                  // Skip rendering if field should not be shown
                  if (!shouldShowField) {
                    return null;
                  }
                  
                  // Multitext: 2 per row (span 3 of 6). Others: 3 per row (span 2 of 6)
                  const isMultitext = field.type === 'multitext';
                  const colSpan = isMultitext ? 'md:col-span-3' : 'md:col-span-2';
                  
                  return (
                    <div key={index} className={`border-b border-gray-200 pb-4 ${colSpan}`}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {field.label}
                        {field.isDeadlineField && (
                          <span className="ml-2 text-xs text-blue-600 font-normal">(Deadline Field)</span>
                        )}
                      </label>
                      <div className="text-sm text-gray-900">
                        {renderFieldValue(field, getFieldValue(field.name))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Checklist */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Checklist</h2>
            {submission.documentVerifications && submission.documentVerifications.length > 0 ? (
              <div className="space-y-4">
                {submission.documentVerifications.map((docVer, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">{docVer.documentName}</h4>
                        {docVer.documentId?.description && (
                          <p className="text-sm text-gray-600 mt-1">{docVer.documentId.description}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Required
                        </span>
                        {docVer.fileUrl && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Available
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {docVer.fileUrl ? (
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-900">{docVer.fileName}</span>
                        </div>
                        <a
                          href={docVer.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Download
                        </a>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No file available</p>
                    )}
                    
                    {docVer.documentId?.category && (
                      <p className="text-xs text-gray-500 mt-2">
                        Category: {docVer.documentId.category}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border border-gray-200 rounded-lg">
                <p className="text-gray-500">No Checklist required for this submission.</p>
              </div>
            )}
          </div>

          {/* Comments */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Comments</h2>
            <div className="space-y-4">
              {/* Initial Comment */}
              {submission.comment && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      Initial Comment
                    </span>
                    <span className="text-sm text-gray-500">
                      {formatDate(submission.createdAt)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{submission.comment}</p>
                </div>
              )}

              {/* Existing Comments */}
              {submissionComments.length > 0 && (
                <div className="space-y-3">
                  {submissionComments.map((comment, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-medium text-gray-900">
                          {comment.createdBy?.firstName} {comment.createdBy?.lastName}
                        </span>
                        <span className="text-sm text-gray-500">
                          {formatDate(comment.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.text}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add Comment Form */}
              <form onSubmit={handleAddComment} className="space-y-2">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={submittingComment || !newComment.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {submittingComment ? 'Adding...' : 'Add Comment'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Document Inventory */}
          {/* Debug Info - Remove after testing */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <h3 className="text-sm font-medium text-yellow-800 mb-2">🔍 Debug Info (Document Inventory)</h3>
            <div className="text-xs text-yellow-700 space-y-1">
              <p><strong>Custom Page exists:</strong> {customPage ? '✅ Yes' : '❌ No'}</p>
              <p><strong>Custom Page ID:</strong> {customPage?._id || 'N/A'}</p>
              <p><strong>Allow Document Inventory (raw):</strong> {String(customPage?.allowDocumentInventory)}</p>
              <p><strong>Allow Document Inventory (type):</strong> {typeof customPage?.allowDocumentInventory}</p>
              <p><strong>Allow Document Inventory (boolean check):</strong> {customPage?.allowDocumentInventory === true ? '✅ TRUE' : '❌ FALSE'}</p>
              <p><strong>Conditional Check Result:</strong> {customPage?.allowDocumentInventory ? '✅ Will Show' : '❌ Will Hide'}</p>
              {customPage && (
                <p><strong>Custom Page Name:</strong> {customPage.name}</p>
              )}
              {customPage?.allowDocumentInventory && (
                <>
                  <p><strong>Max File Size:</strong> {customPage.documentInventorySettings?.maxFileSize || 10}MB</p>
                  <p><strong>Allowed Types:</strong> {customPage.documentInventorySettings?.allowedFileTypes?.join(', ') || 'All'}</p>
                </>
              )}
            </div>
          </div>

          {/* Explicit check with multiple conditions */}
          {customPage && customPage.allowDocumentInventory === true && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Document Inventory</h2>
              
              {/* Documents Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Document Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        File Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uploaded Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uploaded By
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {documentInventory.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                          No documents uploaded yet
                        </td>
                      </tr>
                    ) : (
                      documentInventory.map((doc, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(doc.documentDate).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {doc.originalName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(doc.uploadedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {doc.uploadedBy?.firstName} {doc.uploadedBy?.lastName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <a
                              href={getFullFileUrl(doc.fileUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 mr-3"
                            >
                              View
                            </a>
                            {canUpdate && (
                              <button
                                onClick={() => handleDeleteDocument(doc._id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Upload Form */}
              {canUpdate && (
                <div className="bg-gray-50 p-4 rounded-lg mt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Upload New Document</h3>
                  <form onSubmit={handleDocumentUpload} className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Document Date *
                        </label>
                        <input
                          type="date"
                          value={documentDate}
                          onChange={(e) => setDocumentDate(e.target.value)}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          File *
                        </label>
                        <input
                          type="file"
                          onChange={(e) => setDocumentFile(e.target.files[0])}
                          required
                          accept={customPage.documentInventorySettings?.allowedFileTypes?.join(',') || '*'}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      Max size: {customPage.documentInventorySettings?.maxFileSize || 10}MB
                      {customPage.documentInventorySettings?.allowedFileTypes && 
                        ` | Allowed types: ${customPage.documentInventorySettings.allowedFileTypes.join(', ')}`
                      }
                    </p>
                    <button
                      type="submit"
                      disabled={uploadingDocument}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {uploadingDocument ? 'Uploading...' : 'Upload Document'}
                    </button>
                  </form>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomPageSubmissionDetail; 